import type { FormAnswer, FormField, FormFieldType } from '../fixtures/domain.js';
import { Icon, type IconName } from '../components/Icon.js';
import styles from './FormPreview.module.css';

const MARK: Record<FormFieldType, Record<'off' | 'on', IconName>> = {
  choice: { off: 'radio', on: 'radioOn' },
  multiChoice: { off: 'tickbox', on: 'tickboxOn' },
};

/**
 * A form as the person answering it will see it, and inert. Shared by the
 * library pane, the send dialog, and the thread card, so "is this the one I
 * want to send" reads the same everywhere. With `answers`, the picks are drawn
 * in; with `pickedOnly`, only the picked options render (the answer card).
 */
export function FormPreview({
  fields,
  answers,
  pickedOnly = false,
}: {
  fields: FormField[];
  answers?: FormAnswer[];
  pickedOnly?: boolean;
}) {
  const picked = (fieldId: string, optionId: string) =>
    answers?.some((answer) => answer.fieldId === fieldId && answer.optionIds.includes(optionId)) ??
    false;

  return (
    <ol className={styles.questions}>
      {fields.map((field) => (
        <li key={field.id} className={styles.question}>
          <p className={styles.label}>
            <span>{field.label}</span>
            {/* Said only where several answers is not the default assumption. */}
            {!pickedOnly && field.type === 'multiChoice' && (
              <span className={styles.hint}>Choose any that apply</span>
            )}
          </p>
          <ul className={styles.options}>
            {(pickedOnly
              ? field.options.filter((option) => picked(field.id, option.id))
              : field.options
            ).map((option) => {
              const on = picked(field.id, option.id);
              return (
                <li key={option.id} className={on ? styles.optionPicked : styles.option}>
                  <span className={on ? styles.markPicked : styles.mark}>
                    <Icon name={MARK[field.type][on ? 'on' : 'off']} size={13} />
                  </span>
                  {option.label}
                </li>
              );
            })}
          </ul>
        </li>
      ))}
    </ol>
  );
}
